import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import Lottie from 'lottie-react';
import animationData from '../assets/dash.json';

import StudentLayout from './components/StudentLayout';
import { AuthContext } from '../auth/AuthContext';
import api from '../api/api';
import SupportResourcesPanel from '../shared/SupportResourcesPanel';
import { getCalendarToken } from '../api/users';

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString('en-ZA', { timeZone: 'Africa/Johannesburg' }) : '-';

const formatTime = (value) =>
  value
    ? new Date(value).toLocaleTimeString('en-ZA', {
      timeZone: 'Africa/Johannesburg',
      hour: '2-digit',
      minute: '2-digit',
    })
    : '-';

const StudentDashboard = () => {
  const { token, user } = useContext(AuthContext) ?? {};
  const theme = useTheme();
  const textPrimary = theme.palette.primary.main;
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [calendarFeedUrl, setCalendarFeedUrl] = useState('');
  const meetingWindowRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!token) {
        setError('You need to sign in to view the dashboard.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const { data: sessions } = await api.get('/sessions');
        if (!isMounted) return;
        const list = Array.isArray(sessions) ? sessions : [];
        const now = Date.now();
        const upcomingClasses = list
          .filter((session) => session?.startTime && new Date(session.startTime).getTime() >= now)
          .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

        const completed = list.filter((session) => {
          if (!session?.endTime) return false;
          return new Date(session.endTime).getTime() <= now;
        });

        const totalStudyHours = completed.reduce((acc, session) => {
          if (!session?.startTime || !session?.endTime) return acc;
          const start = new Date(session.startTime);
          const end = new Date(session.endTime);
          if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return acc;
          return acc + Math.max(0, (end.getTime() - start.getTime()) / (1000 * 60 * 60));
        }, 0);

        setSummary({
          totalUpcomingClasses: upcomingClasses.length,
          totalSessions: completed.length,
          totalStudyHours,
          upcomingClasses,
        });
        if (user?._id) {
          const feed = await getCalendarToken(user._id);
          setCalendarFeedUrl(feed?.feedUrl || '');
        }
      } catch (err) {
        console.error('[StudentDashboard] failed to load summary', err);
        if (isMounted) {
          setError(err?.response?.data?.message || err?.message || 'Failed to load dashboard data.');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [token, user?._id]);

  const upcomingClasses = summary?.upcomingClasses ?? [];
  const nextClass = upcomingClasses[0];

  const totalStudyHours = useMemo(
    () => Number(summary?.totalStudyHours || 0).toFixed(1),
    [summary?.totalStudyHours]
  );

  const canJoinNextClass = useMemo(() => {
    if (!nextClass?.startTime) return false;
    const now = new Date();
    const start = new Date(nextClass.startTime);
    const diffMinutes = (start.getTime() - now.getTime()) / (1000 * 60);
    if (diffMinutes > 5) return false;

    const end = nextClass.endTime ? new Date(nextClass.endTime) : new Date(start.getTime() + 130 * 60 * 1000);
    return now <= end;
  }, [nextClass]);

  const handleJoinNextClass = async () => {
    if (!nextClass?._id || !user) return;
    const preOpened = window.open('', '_blank');
    try {
      const { data } = await api.get(`/sessions/${nextClass._id}/join`);
      const joinUrl = data?.url || data?.joinUrl;
      if (!joinUrl) {
        if (preOpened) preOpened.close();
        alert('Join URL not available yet.');
        return;
      }
      if (preOpened) {
        preOpened.location = joinUrl;
        meetingWindowRef.current = preOpened;
        setTimeout(() => {
          try {
            if (meetingWindowRef.current && !meetingWindowRef.current.closed) {
              meetingWindowRef.current.close();
            }
          } catch {}
        }, 60 * 60 * 1000);
      } else {
        window.location.assign(joinUrl);
      }
    } catch (err) {
      console.error('[StudentDashboard] join failed', err);
      if (preOpened) preOpened.close();
      alert('Failed to join the session.');
    }
  };

  const renderUpcoming = () => {
    if (loading) {
      return (
        <List>
          {Array.from({ length: 3 }).map((_, idx) => (
            <ListItem key={idx} divider disableGutters>
              <ListItemText
                primary={<Skeleton width="60%" />}
                secondary={<Skeleton width="40%" />}
              />
            </ListItem>
          ))}
        </List>
      );
    }

    if (!upcomingClasses.length) {
      return (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No upcoming classes scheduled. Once your educator schedules a session, it will appear here.
        </Alert>
      );
    }

    return (
      <List disablePadding>
        {upcomingClasses.map((session) => (
          <ListItem
            key={session._id || session.id || session.startTime}
            divider
            disableGutters
            sx={{ py: 1.5 }}
          >
            <ListItemText
              primary={
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="subtitle1" fontWeight={700}>
                    {session.subject || 'Scheduled session'}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: { xs: 1, sm: 0 } }}>
                    {session.program && (
                      <Chip size="small" label={session.program.toUpperCase()} />
                    )}
                    {session.gradeLevel != null && (
                      <Chip size="small" label={`Grade ${session.gradeLevel}`} />
                    )}
                  </Stack>
                </Stack>
              }
              secondary={
                <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(session.startTime)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatTime(session.startTime)}
                  </Typography>
                </Stack>
              }
            />
          </ListItem>
        ))}
      </List>
    );
  };

  const handleCopyFeed = async () => {
    if (!calendarFeedUrl) return;
    try {
      await navigator.clipboard.writeText(calendarFeedUrl);
    } catch {}
  };

  return (
    <StudentLayout>
      <Box>
        {loading && !error ? (
          <Stack alignItems="center" py={6} spacing={3}>
            <CircularProgress size={48} />
            <Typography variant="body1" color="text.secondary">
              Loading your dashboard...
            </Typography>
          </Stack>
 ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
 ) : null}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card elevation={6} sx={{ overflow: 'hidden' }}>
              <Grid container>
                <Grid item xs={12} md={7}>
                  <CardContent>
                    <Stack spacing={2}>
                      <Typography variant="h4" fontWeight={800} sx={{ color: textPrimary }}>
                        Welcome back, {user?.name || 'Student'}!
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Stay on top of your learning journey. Join upcoming classes, revisit recordings, and
                        keep an eye on your progress.
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Chip icon={<SchoolRoundedIcon fontSize="small" />} label="Learning in progress" />
                        <Chip icon={<ScheduleRoundedIcon fontSize="small" />} label="Local time zone" />
                      </Stack>
                    </Stack>
                  </CardContent>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={5}
                  sx={{
                    bgcolor: (theme) => theme.palette.secondary.light,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box sx={{ width: '100%', maxWidth: 280, px: 3, py: 2 }}>
                    <Lottie animationData={animationData} loop />
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card elevation={4} sx={{ height: '100%' }}>
              <CardHeader
                avatar={<EventAvailableRoundedIcon color="primary" />}
                title={<Typography variant="subtitle2" sx={{ color: textPrimary }}>Upcoming sessions</Typography>}
              />
              <CardContent>
                <Typography variant="h4" fontWeight={800} sx={{ color: textPrimary }}>
                  {loading ? <Skeleton width={80} height={42} /> : summary?.totalUpcomingClasses ?? 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  These are the sessions scheduled for you in the near future.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={4} sx={{ height: '100%' }}>
              <CardHeader
                avatar={<SchoolRoundedIcon color="primary" />}
                title={<Typography variant="subtitle2" sx={{ color: textPrimary }}>Total sessions attended</Typography>}
              />
              <CardContent>
                <Typography variant="h4" fontWeight={800} sx={{ color: textPrimary }}>
                  {loading ? <Skeleton width={80} height={42} /> : summary?.totalSessions ?? 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Completed classes since you joined the programme.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={4} sx={{ height: '100%' }}>
              <CardHeader
                avatar={<ScheduleRoundedIcon color="primary" />}
                title={<Typography variant="subtitle2" sx={{ color: textPrimary }}>Study hours logged</Typography>}
              />
              <CardContent>
                <Typography variant="h4" fontWeight={800} sx={{ color: textPrimary }}>
 {loading ? <Skeleton width={80} height={42} /> : `${totalStudyHours}h`}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Total time you have spent in recorded educator sessions.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Card elevation={4} sx={{ height: '100%' }}>
              <CardHeader
                title={<Typography variant="h6" sx={{ color: textPrimary }}>Your next session</Typography>}
                subheader="Join when your educator starts the class"
                subheaderTypographyProps={{ sx: { color: 'text.secondary' } }}
              />
              <CardContent>
                {loading ? (
                  <Stack spacing={2}>
                    <Skeleton height={40} />
                    <Skeleton height={24} width="60%" />
                    <Skeleton height={36} width="40%" />
                  </Stack>
 ) : !nextClass ? (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Your next class will appear here once it's scheduled.
                  </Alert>
 ) : (
                  <Stack spacing={2}>
                    <Typography variant="h5" fontWeight={700}>
                      {nextClass.subject || 'Class Session'}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Chip label={formatDate(nextClass.startTime)} />
                      <Chip label={formatTime(nextClass.startTime)} />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Educator:{' '}
                      <strong>
                        {nextClass.tutorName ||
                          nextClass.tutor?.name ||
                          nextClass.tutor?.fullName ||
                          'your educator'}
                      </strong>
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<PlayCircleFilledRoundedIcon />}
                      onClick={handleJoinNextClass}
                      disabled={!canJoinNextClass}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      Join Session
                    </Button>
                    {!canJoinNextClass && (
                      <Typography variant="caption" color="text.secondary">
                        The join link becomes active within 5 minutes of the start time.
                      </Typography>
                    )}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={7}>
            <Card elevation={4} sx={{ height: '100%' }}>
              <CardHeader
                title={<Typography variant="h6" sx={{ color: textPrimary }}>Upcoming classes</Typography>}
                subheader="Plan ahead for the week"
                subheaderTypographyProps={{ sx: { color: 'text.secondary' } }}
              />
              <CardContent>{renderUpcoming()}</CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card elevation={4}>
              <CardHeader
                title={<Typography variant="h6" sx={{ color: textPrimary }}>Calendar feed</Typography>}
                subheader="Add your schedule to Google Calendar, Apple Calendar, or Outlook."
                subheaderTypographyProps={{ sx: { color: 'text.secondary' } }}
              />
              <CardContent>
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    Use this link to subscribe to your timetable.
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                    <TextField
                      value={calendarFeedUrl || 'Generating feed link...'}
                      fullWidth
                      InputProps={{ readOnly: true }}
                    />
                    <Button variant="outlined" onClick={handleCopyFeed} disabled={!calendarFeedUrl}>
                      Copy link
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <SupportResourcesPanel userRole="student" />
          </Grid>
        </Grid>
      </Box>
    </StudentLayout>
  );
};

export default StudentDashboard;







